import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from 'react';
import { User, Session, AuthChangeEvent } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { Publisher } from '../types/slicetv';
import { Database } from '../types/supabase-types';

export type PublisherMembership =
  Database['public']['Tables']['publisher_users']['Row'];

export type StudioAccess = {
  publisher: Publisher;
  membership: PublisherMembership | null;
};

const ACTIVE_STUDIO_STORAGE_KEY = 'kinora_active_publisher_id';

// Minimal context - ONLY state management
interface AuthContextType {
  user: User | null;
  session: Session | null;
  publisher: Publisher | null;
  /** All studios the user can access (founder + team memberships) */
  studios: StudioAccess[];
  /** Set when access comes via publisher_users team membership (guild-invited members) */
  membership: PublisherMembership | null;
  initializing: boolean;
  publisherLoading: boolean;
  loading: boolean;
  refreshPublisher: () => Promise<void>;
  setActiveStudio: (publisherId: string) => void;
  isAuthenticated: () => boolean;
  isPublisherApproved: () => boolean;
  canAccessPublisherPortal: () => boolean;
  canManageTeam: () => boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [publisher, setPublisher] = useState<Publisher | null>(null);
  const [studios, setStudios] = useState<StudioAccess[]>([]);
  const [membership, setMembership] = useState<PublisherMembership | null>(null);
  const [initializing, setInitializing] = useState(true);
  const [publisherLoading, setPublisherLoading] = useState(false);
  const [loading, setLoading] = useState(false);

  const applyActiveStudio = useCallback((options: StudioAccess[]) => {
    setStudios(options);
    if (options.length === 0) {
      setPublisher(null);
      setMembership(null);
      return;
    }
    const savedId = localStorage.getItem(ACTIVE_STUDIO_STORAGE_KEY);
    const active =
      options.find(s => s.publisher.id === savedId) ?? options[0];
    setPublisher(active.publisher);
    setMembership(active.membership);
  }, []);

  // Resolve all studios: founder/owner + team memberships
  const fetchPublisherData = useCallback(async (userId: string) => {
    setPublisherLoading(true);
    try {
      const options: StudioAccess[] = [];

      const { data: owned, error: ownedError } = await supabase
        .from('publishers')
        .select('*')
        .eq('auth_user_id', userId)
        .maybeSingle();

      if (ownedError) {
        console.warn('⚠️ Publisher fetch error:', ownedError.message);
      }
      if (owned) {
        options.push({ publisher: owned, membership: null });
      }

      const { data: memberRows, error: memberError } = await supabase
        .from('publisher_users')
        .select('*, publishers(*)')
        .eq('auth_user_id', userId)
        .eq('is_active', true)
        .order('created_at');

      if (memberError) {
        console.warn('⚠️ Membership fetch error:', memberError.message);
      }

      for (const row of memberRows ?? []) {
        const studio = row.publishers as Publisher | null;
        if (studio && !options.some(o => o.publisher.id === studio.id)) {
          options.push({ publisher: studio, membership: row });
        }
      }

      applyActiveStudio(options);
    } catch (error) {
      console.warn('⚠️ Publisher fetch error:', error);
      applyActiveStudio([]);
    } finally {
      setPublisherLoading(false);
    }
  }, [applyActiveStudio]);

  const setActiveStudio = useCallback((publisherId: string) => {
    const match = studios.find(s => s.publisher.id === publisherId);
    if (!match) return;
    localStorage.setItem(ACTIVE_STUDIO_STORAGE_KEY, publisherId);
    setPublisher(match.publisher);
    setMembership(match.membership);
  }, [studios]);

  // Initialize auth state
  useEffect(() => {
    let mounted = true;

    const initializeAuth = async () => {
      try {
        console.log('🔵 Starting auth initialization...');

        const {
          data: { session },
          error,
        } = await supabase.auth.getSession();

        if (error) {
          console.error('❌ Error getting session:', error);
        }

        if (mounted) {
          console.log(
            '✅ Session retrieved:',
            session ? 'logged in' : 'no session'
          );
          setSession(session);
          setUser(session?.user ?? null);

          // Fetch publisher data in background, don't block initialization
          if (session?.user) {
            console.log('🔵 Fetching publisher data for user:', session.user.id);
            fetchPublisherData(session.user.id).catch(err => {
              console.warn('⚠️ Background publisher fetch failed:', err);
            });
          }

          setInitializing(false);
        }
      } catch (error) {
        console.error('❌ Auth initialization error:', error);
        if (mounted) {
          setInitializing(false);
        }
      }
    };

    initializeAuth();

    // Listen for auth state changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(
      async (_event: AuthChangeEvent, session: Session | null) => {
        if (!mounted) return;

        setSession(session);
        setUser(session?.user ?? null);

        if (session?.user) {
          fetchPublisherData(session.user.id).catch(err => {
            console.warn('⚠️ Auth state change publisher fetch failed:', err);
          });
        } else {
          setPublisher(null);
          setStudios([]);
          setMembership(null);
          setPublisherLoading(false);
        }
      }
    );

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [fetchPublisherData]);

  const refreshPublisher = async () => {
    if (user?.id) {
      await fetchPublisherData(user.id);
    }
  };

  const isAuthenticated = () => {
    return !!user && !!session;
  };

  const isPublisherApproved = () => {
    return !!publisher && publisher.status === 'approved' && publisher.is_active === true;
  };

  const canAccessPublisherPortal = () => {
    if (!publisher) return false;
    // Guild-founded studios get probationary access; contract gates publishing instead.
    if (publisher.origin === 'guild') return true;
    // Contract signed = full portal access. Stripe is a checklist item, not a gate.
    return publisher.contract_signed === true;
  };

  const canManageTeam = () => {
    if (!publisher) return false;
    if (!membership) return publisher.auth_user_id === user?.id; // founder
    return (
      membership.role === 'owner' ||
      membership.role === 'admin' ||
      membership.can_manage_team === true
    );
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  const contextValue: AuthContextType = {
    user,
    session,
    publisher,
    studios,
    membership,
    initializing,
    publisherLoading,
    loading,
    refreshPublisher,
    setActiveStudio,
    isAuthenticated,
    isPublisherApproved,
    canAccessPublisherPortal,
    canManageTeam,
    signOut,
  };

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}
